/**
 * Decimal km radius field — G-RADIUS step=0.1 / inputMode=decimal.
 */
import type { ChangeEvent } from 'react';
import {
  MAP_VIEW_RADIUS_KM_DEFAULT,
  MAP_VIEW_RADIUS_KM_MAX,
  MAP_VIEW_RADIUS_KM_MIN,
  parseRadiusKm,
  type ParseRadiusKmError,
} from '../shopMapViewModel.js';

export interface ShopMapRadiusEditorProps {
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly label: string;
  readonly ariaLabel: string;
  readonly invalidMessage: string | null;
  readonly testId?: string;
  readonly id?: string;
  readonly disabled?: boolean;
}

export function radiusErrorMessageKey(
  error: ParseRadiusKmError,
): 'directory.mapView.radiusInvalid' | 'directory.mapView.radiusTooSmall' | 'directory.mapView.radiusTooLarge' | 'directory.mapView.radiusTooManyDecimals' {
  switch (error) {
    case 'tooSmall':
      return 'directory.mapView.radiusTooSmall';
    case 'tooLarge':
      return 'directory.mapView.radiusTooLarge';
    case 'tooManyDecimals':
      return 'directory.mapView.radiusTooManyDecimals';
    case 'invalid':
    default:
      return 'directory.mapView.radiusInvalid';
  }
}

export function ShopMapRadiusEditor({
  value,
  onChange,
  label,
  ariaLabel,
  invalidMessage,
  testId = 'customer-shops-directory-around-me-km',
  id,
  disabled = false,
}: ShopMapRadiusEditorProps): JSX.Element {
  const describedBy = invalidMessage !== null ? `${testId}-error` : undefined;

  return (
    <label className="flex min-w-0 flex-col gap-1 text-xs text-[var(--color-on-surface-muted)]">
      <span>{label}</span>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        step="0.1"
        min={MAP_VIEW_RADIUS_KM_MIN}
        max={MAP_VIEW_RADIUS_KM_MAX}
        value={value}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={invalidMessage !== null}
        aria-describedby={describedBy}
        data-testid={testId}
        className="min-h-11 w-full rounded-sm border border-[var(--color-border-strong)] bg-[var(--color-surface-elevated)] px-3 py-2 text-sm text-[var(--color-on-surface)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          onChange(event.target.value);
        }}
      />
      {invalidMessage !== null ? (
        <span
          id={`${testId}-error`}
          className="text-xs text-[var(--color-danger)]"
          role="alert"
          data-testid={`${testId}-error`}
        >
          {invalidMessage}
        </span>
      ) : null}
    </label>
  );
}

export function defaultRadiusKmDraft(): string {
  return String(MAP_VIEW_RADIUS_KM_DEFAULT);
}

export function validateRadiusKmDraft(raw: string): ParseRadiusKmError | null {
  const parsed = parseRadiusKm(raw);
  return parsed.ok ? null : parsed.error;
}
